import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import LabeledTextInput from 'components/consumer/LabeledTextInput';

// Format a phone number input into (XXX) XXX-XXXX or 1 (XXX) XXX-XXXX.
// A leading 1 is treated as the US country code only when the total digit
// count exceeds 10 (e.g. 12345678910 → "1 (234) 567-8910").
function formatPhoneNumber(input) {
  const cleaned = input.replace(/[^\d]/g, '');

  if (cleaned.length === 0) {
    return '';
  }

  if (cleaned.startsWith('1') && cleaned.length > 10) {
    const phoneDigits = cleaned.slice(1, 11);
    const area = phoneDigits.slice(0, 3);
    const mid = phoneDigits.slice(3, 6);
    const end = phoneDigits.slice(6, 10);
    return `1 (${area}) ${mid}-${end}`;
  }

  const limited = cleaned.slice(0, 10);
  const area = limited.slice(0, 3);
  const mid = limited.slice(3, 6);
  const end = limited.slice(6, 10);

  if (limited.length <= 3) {
    return `(${area}`;
  }
  if (limited.length <= 6) {
    return `(${area}) ${mid}`;
  }
  return `(${area}) ${mid}-${end}`;
}

function firstIndexForDigitCount(formatted, digitCount, index = 0, count = 0) {
  if (index >= formatted.length || count === digitCount) {
    return index;
  }
  return firstIndexForDigitCount(
    formatted,
    digitCount,
    index + 1,
    count + (/\d/.test(formatted[index]) ? 1 : 0)
  );
}

function findCursorPosition(formatted, digitCount, rawCursorPos) {
  const regionStart = firstIndexForDigitCount(formatted, digitCount);

  const regionEnd = Math.max(
    regionStart,
    firstIndexForDigitCount(formatted, digitCount + 1) - 1
  );
  return Math.max(regionStart, Math.min(rawCursorPos, regionEnd));
}

const PhoneNumberField = ({
  id = 'phone_number',
  name = 'phone_number',
  placeholder = 'Phone (delivery contact) *',
  value,
  error,
  isTouched,
  handleChange,
  handleBlur,
  updateShipping,
  isRequired = true,
  ...props
}) => {
  const inputRef = useRef(null);
  const desiredCursorRef = useRef(null);

  useLayoutEffect(() => {
    if (desiredCursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        desiredCursorRef.current,
        desiredCursorRef.current
      );
      desiredCursorRef.current = null;
    }
  });

  const onChangeHandler = evt => {
    const input = evt.target;

    const cursorPos = input.selectionStart ?? input.value.length;

    const digitsBeforeCursor = input.value
      .slice(0, cursorPos)
      .replace(/[^\d]/g, '').length;

    const initialDigits = input.value.replace(/[^\d]/g, '');

    const isCountryCode =
      initialDigits.startsWith('1') && initialDigits.length > 10;
    const maxDigits = isCountryCode ? 11 : 10;

    const rawDigits = initialDigits.slice(0, maxDigits);

    const formatted = formatPhoneNumber(rawDigits);
    evt.target.value = formatted;

    desiredCursorRef.current = findCursorPosition(
      formatted,
      digitsBeforeCursor,
      cursorPos
    );

    if (handleChange) {
      handleChange(evt);
    }
  };

  return (
    <LabeledTextInput
      ref={inputRef}
      id={id}
      name={name}
      type="tel"
      value={value}
      label={placeholder}
      data-xc="shipping-phoneNumber-field"
      onChange={onChangeHandler}
      autoComplete="tel"
      onBlur={evt => {
        handleBlur(evt);
        if (!error && updateShipping) {
          updateShipping();
        }
      }}
      required={isRequired ? '' : undefined}
      aria-required={isRequired ? '' : undefined}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      errorMessage={isTouched && error ? error : null}
    />
  );
};

PhoneNumberField.propTypes = {
  error: PropTypes.string,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  id: PropTypes.string,
  isRequired: PropTypes.bool,
  isTouched: PropTypes.bool,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  updateShipping: PropTypes.func,
  value: PropTypes.string,
};

export default PhoneNumberField;
